/**
 * «In breve» — one or two plain sentences saying WHY a store receives the quantity it receives.
 *
 * **The grain is the store × article, not the size.** The engine decides one quantity per store ×
 * article (the sum over the sizes) and only then splits it over the sizes with the size curve. So
 * the answer is computed on EVERY size line of the same store and article in the run, even where
 * the surface shows one size: U = Σ units, R = Σ requested_units, the drivers summed by code
 * (pairs add), the set of `bound_by` codes, and the run's ceiling per store and article.
 *
 * **The ceiling is the run's, not today's.** `allocation.maximum_units_per_cell` is read as the
 * run recorded it (`GET /inseason/runs/{run_id}/provenance`), never from the registry now. When the
 * run recorded none, or recorded two different values, the ceiling is unknown and the sentence
 * says so instead of choosing a cause (`capOfRun`).
 *
 * **No sentence names a cause the row's data does not prove.** A shortfall is attributed to the
 * NEGATIVE engine drivers only (platform ADR 6038), in pairs, largest first. What the drivers do
 * not say — why the allocation cut pairs while the distribution centre still had stock — is
 * stated as the alternatives that remain, or as the constraint code when one is recorded.
 *
 * **A row that asked for nothing is explained from the NEED drivers alone** (platform ADR 6038,
 * verified on `planning/replenishment/explain.py`): `on_hand` and `in_transit` are the stock held
 * with the sign taken off (a contribution of −8 is 8 pairs held), `cover_target` is the service
 * quantity less the forecast (it can be negative), and `whole_units` is the floor at nought — so the
 * need terms sum to the request, which is the need CLIPPED at nought, not the raw need. Stock on
 * hand and in transit that reaches forecast + cover + presentation minimum is therefore what a
 * request of nought means; when the drivers are missing, unknown or do not add up, the sentence
 * says the run does not say why, never a guess.
 *
 * **The rationing, only where the run recorded it** (bata-platform issue 603, ADR 6074,
 * `docs/adr/6021`). A shortfall the DC's stock cut says how much of those sizes the DC held against
 * how many pairs every door asked for — summed over the cut sizes, and said only when EVERY cut size
 * recorded its pool and the pool was indeed short of the asks. A shortfall the DC or the allocation
 * cut says the door's place in the engine's order, only when every size of the row records the same
 * place. Neither is said as a cause: they are the figures the cut was made against.
 *
 * Pure: a list of phrases (a catalogue key and its values) that the shared component renders.
 */

import type { ParameterUsed, ProposalSummary } from '@/api/generated';

import { DROPPED_BOUND_BY, isDroppedDoorLine } from './dropped-door';
import { dcPool, sharedRank } from './rationing';
import { NEED_DRIVER_CODES } from './store-article-rows';
import { lineCoverage } from './uncovered';

/** The driver codes that are the engine's change to the need (platform ADR 6038). */
const DC_AVAILABILITY = 'dc_availability';
const CASE_ROUNDING = 'case_rounding';
const ALLOCATION = 'allocation';

/** The `bound_by` codes the sentences read. */
const PRES_MIN_FLOOR = 'pres_min_floor';
const CASE_ROUNDED = 'case_rounded';
const SIZE_RUN_DROPPED = DROPPED_BOUND_BY;
const CAPACITY_CAPPED = 'capacity_capped';

/** The registry key of the ceiling the engine applies to the store × article total. */
export const MAXIMUM_UNITS_KEY = 'allocation.maximum_units_per_cell';

/**
 * Every phrase, as a catalogue key. Spelled out as literals so the catalogue check sees each one —
 * nothing here is built by interpolation.
 */
export const ANSWER_KEY = {
  covered: 'inseason.answer.covered',
  split: 'inseason.answer.split',
  overFloor: 'inseason.answer.overFloor',
  overCase: 'inseason.answer.overCase',
  over: 'inseason.answer.over',
  floorOnly: 'inseason.answer.floorOnly',
  shortDc: 'inseason.answer.shortDc',
  shortCase: 'inseason.answer.shortCase',
  dropped: 'inseason.answer.dropped',
  droppedDoor: 'inseason.answer.droppedDoor',
  shortCapacity: 'inseason.answer.shortCapacity',
  shortCap: 'inseason.answer.shortCap',
  shortCapOrObjective: 'inseason.answer.shortCapOrObjective',
  shortObjective: 'inseason.answer.shortObjective',
  noPairsCovered: 'inseason.answer.noPairsCovered',
  noPairsTarget: 'inseason.answer.noPairsTarget',
  noPairsNoNeed: 'inseason.answer.noPairsNoNeed',
  noPairsUnexplained: 'inseason.answer.noPairsUnexplained',
  noDecomposition: 'inseason.uncovered.noDecomposition',
  noEngineDriver: 'inseason.uncovered.noEngineDriver',
  dcPool: 'inseason.answer.dcPool',
  rationingRank: 'inseason.answer.rationingRank',
} as const;

export type AnswerId = keyof typeof ANSWER_KEY;

/** One sentence: which one, its catalogue key and the numbers the key names. */
export interface AnswerPhrase {
  readonly id: AnswerId;
  readonly key: (typeof ANSWER_KEY)[AnswerId];
  readonly values?: Readonly<Record<string, number>>;
}

type AnswerLine = Pick<ProposalSummary,
  | 'units' | 'requested_units' | 'bound_by' | 'drivers'
  | 'rationing_rank' | 'rationing_doors' | 'rationing_rank_absent_reason'
  | 'dc_pool_units' | 'dc_pool_requested_units' | 'dc_pool_after_units' | 'dc_pool_absent_reason'
  | 'size_curve_share' | 'size_curve_share_absent_reason'>
  /** Published by the platform on every line; optional here so a caller holding fewer fields still asks. */
  & Partial<Pick<ProposalSummary, 'size_key' | 'position_grain'>>;


/**
 * The DC's pool over the sizes its stock cut (a negative `dc_availability` driver): pairs held and
 * pairs every door asked for, summed — or `null` unless every cut size recorded its pool and the
 * pool was short of the asks, which is what the figures must show before a sentence states them.
 */
function poolOfCutSizes(lines: readonly AnswerLine[]): { readonly pool: number; readonly asked: number } | null {
  const cut = lines.filter((line) => (line.drivers ?? []).some((driver) => driver.code === DC_AVAILABILITY && driver.contribution < 0));
  if (cut.length === 0) return null;
  let pool = 0;
  let asked = 0;
  for (const line of cut) {
    const recorded = dcPool(line);
    if (recorded.kind === 'absent') return null;
    pool += recorded.value.before;
    asked += recorded.value.asked;
  }
  return pool < asked ? { pool, asked } : null;
}

function phrase(id: AnswerId, values?: Readonly<Record<string, number>>): AnswerPhrase {
  return values === undefined ? { id, key: ANSWER_KEY[id] } : { id, key: ANSWER_KEY[id], values };
}

/**
 * The lines of the proposal's own store × article: its sibling size lines where the surface holds
 * them, else the proposal alone. A sibling list that does not contain the proposal is stale, and is
 * not trusted over it.
 */
export function rowLinesOf(proposal: ProposalSummary, siblings?: readonly ProposalSummary[]): readonly ProposalSummary[] {
  if (siblings === undefined) return [proposal];
  const row = siblings.filter((line) => line.store_key === proposal.store_key && line.article_key === proposal.article_key);
  return row.some((line) => line.proposal_id === proposal.proposal_id) ? row : [proposal];
}

/**
 * The ceiling per store and article AS THE RUN USED IT: the one numeric value recorded for
 * `allocation.maximum_units_per_cell` across the run's stages, or `null` when none was recorded or
 * the stages recorded different ones.
 */
export function capOfRun(
  provenance: {
    readonly run?: { readonly parameters?: readonly Pick<ParameterUsed, 'key' | 'numeric_value'>[] | undefined } | undefined;
    readonly reforecast?: { readonly parameters?: readonly Pick<ParameterUsed, 'key' | 'numeric_value'>[] | undefined } | undefined;
  } | undefined,
): number | null {
  if (provenance === undefined) return null;
  const values = new Set<number>();
  for (const stage of [provenance.run, provenance.reforecast]) {
    for (const parameter of stage?.parameters ?? []) {
      if (parameter.key === MAXIMUM_UNITS_KEY && parameter.numeric_value !== null && Number.isFinite(parameter.numeric_value)) {
        values.add(parameter.numeric_value);
      }
    }
  }
  const [only] = [...values];
  return values.size === 1 && only !== undefined ? only : null;
}

/** The drivers summed by code over the row's lines — pairs, so they add. */
function sumDrivers(lines: readonly AnswerLine[]): readonly { readonly code: string; readonly contribution: number }[] {
  const sums = new Map<string, number>();
  for (const line of lines) {
    for (const driver of line.drivers ?? []) sums.set(driver.code, (sums.get(driver.code) ?? 0) + driver.contribution);
  }
  return [...sums.entries()].map(([code, contribution]) => ({ code, contribution }));
}

/** The sums of a row's need drivers agree with a request of nought to this many pairs (rowWaterfall's). */
const BALANCE_TOLERANCE = 1e-6;
/** The figures of the «asked for nothing» sentence are shown to a tenth of a pair. */
const tenth = (pairs: number): number => (Math.round(pairs * 10) / 10) || 0; // `|| 0`: a stock of −0 must not read «-0»

/**
 * Why a row that asked for nothing and got nothing asked for nothing — from its need drivers alone.
 * Proved only when every driver is a need driver, they add up to the request (nought), the stock
 * held is not negative and reaches the target (forecast + cover + presentation minimum). Else the
 * run does not say.
 */
function noPairsAnswer(drivers: readonly { readonly code: string; readonly contribution: number }[]): AnswerPhrase {
  const need: ReadonlySet<string> = new Set(NEED_DRIVER_CODES);
  const unexplained = phrase('noPairsUnexplained');
  if (drivers.length === 0 || drivers.some((driver) => !need.has(driver.code))) return unexplained;
  const sum = (code: string): number => drivers.filter((driver) => driver.code === code).reduce((total, driver) => total + driver.contribution, 0);
  if (Math.abs(drivers.reduce((total, driver) => total + driver.contribution, 0)) > BALANCE_TOLERANCE) return unexplained;
  const forecast = sum('forecast_rate');
  const cover = sum('cover_target');
  const presentationMinimum = sum('presentation_minimum');
  // The stock is the drivers' own sign taken off: a contribution of −8 is 8 pairs held.
  const onHand = -sum('on_hand');
  const inTransit = -sum('in_transit');
  const target = forecast + cover + presentationMinimum;
  if (forecast < 0 || presentationMinimum < 0 || onHand < 0 || inTransit < 0) return unexplained;
  if (onHand + inTransit < target - BALANCE_TOLERANCE) return unexplained;
  if (target <= BALANCE_TOLERANCE) return phrase('noPairsNoNeed');
  // A service quantity below the forecast (a slow size) leaves no cover term to name: say the target.
  if (cover < 0) return phrase('noPairsTarget', { onHand: tenth(onHand), inTransit: tenth(inTransit), target: tenth(target) });
  return phrase('noPairsCovered', {
    onHand: tenth(onHand), inTransit: tenth(inTransit), forecast: tenth(forecast), cover: tenth(cover), presentationMinimum: tenth(presentationMinimum),
  });
}

/** Order of the engine's three cuts when two cut the same number of pairs. */
const CUT_ORDER: readonly string[] = [DC_AVAILABILITY, CASE_ROUNDING, ALLOCATION];

/**
 * The row's answer. `lines` are ALL the size lines of one store × article; `cap` is the run's
 * recorded ceiling, or `null` when unknown. An empty row has nothing to say; a row that asked for
 * nothing and got nothing says why from its need drivers, or that the run does not.
 */
export function plainAnswer(lines: readonly AnswerLine[], cap: number | null): readonly AnswerPhrase[] {
  if (lines.length === 0) return [];
  // A dropped door's line is the store × article's whole answer: fewer pairs than the article's core
  // sizes, so no size run can be sent and nothing is proposed. It is said as that, with the need it
  // leaves visible — before the rules below, which read a request against a proposal by size.
  const droppedNeed = lines.filter(isDroppedDoorLine).reduce((sum, line) => sum + line.requested_units, 0);
  if (droppedNeed > 0) {
    return [phrase('droppedDoor', { requested: droppedNeed }), ...plainAnswer(lines.filter((line) => !isDroppedDoorLine(line)), cap)];
  }
  const units = lines.reduce((sum, line) => sum + line.units, 0);
  const requested = lines.reduce((sum, line) => sum + line.requested_units, 0);
  const bound = new Set(lines.map((line) => line.bound_by));
  const drivers = sumDrivers(lines);

  // A. The row got what it asked for, or more.
  if (units >= requested) {
    if (units === requested) {
      if (requested === 0) return [noPairsAnswer(drivers)];
      return lines.some((line) => line.units < line.requested_units)
        ? [phrase('split')]
        : [phrase('covered', { requested })];
    }
    const extra = units - requested;
    if (bound.has(PRES_MIN_FLOOR)) return [phrase('overFloor', { extra })];
    const caseUp = bound.has(CASE_ROUNDED) || drivers.some((driver) => driver.code === CASE_ROUNDING && driver.contribution > 0);
    return [phrase(caseUp ? 'overCase' : 'over', { extra })];
  }

  // B. A shortfall: the missing pairs are the engine's negative drivers, nothing else.
  const out: AnswerPhrase[] = [];
  if (bound.has(PRES_MIN_FLOOR)) out.push(phrase('floorOnly'));

  const droppedNothing = bound.has(SIZE_RUN_DROPPED) && units === 0;
  const coverage = lineCoverage({ requested_units: requested, units, drivers });
  const cuts: { readonly code: string; readonly pairs: number }[] = coverage.kind === 'short'
    ? coverage.reasons
      .map((reason) => ({ code: reason.code, pairs: Math.round(-reason.contribution) }))
      .filter((cut) => cut.pairs >= 1)
    : [];
  // A store left out of the article entirely is proven by the code on its lines even where the
  // run published no allocation driver for it.
  if (droppedNothing && !cuts.some((cut) => cut.code === ALLOCATION)) cuts.push({ code: ALLOCATION, pairs: 0 });
  cuts.sort((a, b) => b.pairs - a.pairs || CUT_ORDER.indexOf(a.code) - CUT_ORDER.indexOf(b.code));

  for (const cut of cuts) {
    if (cut.code === DC_AVAILABILITY) out.push(phrase('shortDc', { pairs: cut.pairs }));
    else if (cut.code === CASE_ROUNDING) out.push(phrase('shortCase', { pairs: cut.pairs }));
    else if (droppedNothing) out.push(phrase('dropped'));
    else if (bound.has(CAPACITY_CAPPED)) out.push(phrase('shortCapacity', { pairs: cut.pairs }));
    else if (cap !== null && units >= cap) out.push(phrase('shortCap', { pairs: cut.pairs, cap }));
    else if (cap === null) out.push(phrase('shortCapOrObjective', { pairs: cut.pairs }));
    else out.push(phrase('shortObjective', { pairs: cut.pairs }));
  }

  // What the DC's cut was made against, and the door's place in the order — only where recorded.
  if (cuts.some((cut) => cut.code === DC_AVAILABILITY)) {
    const pool = poolOfCutSizes(lines);
    if (pool !== null) out.push(phrase('dcPool', pool));
  }
  if (cuts.some((cut) => cut.code === DC_AVAILABILITY || cut.code === ALLOCATION)) {
    const rank = sharedRank(lines);
    if (rank !== null && rank.kind === 'value') out.push(phrase('rationingRank', { rank: rank.value.rank, doors: rank.value.doors }));
  }

  if (cuts.length === 0) {
    // No engine driver to attribute the missing pairs to: say that, with the existing words.
    out.push(phrase(drivers.length === 0 ? 'noDecomposition' : 'noEngineDriver'));
  }
  return out;
}
